import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { LanguageAndFormatsFormValues } from '../LanguageAndFormatsForm/typings'

import { selectUserConfiguration, updateSessionUserConfiguration } from '@/features/domain/user'
import { useIsUnmounted, useNotification } from '@/hooks'
import { intl } from '@/intl'
import { useAppDispatch } from '@/store'

export function useLanguageAndFormatsData() {
  const userConfiguration = useSelector(selectUserConfiguration)
  const dispatch = useAppDispatch()
  const toast = useNotification()
  const isUnMounted = useIsUnmounted()

  const languageAndPreferences = useMemo<LanguageAndFormatsFormValues>(() => {
    return {
      distanceFormat: userConfiguration.distanceFormat,
      dateFormat: userConfiguration.dateFormat,
      timeFormat: userConfiguration.timeFormat,
      currency: userConfiguration.currency,
      language: userConfiguration.language,
    }
  }, [userConfiguration])

  const updateLanguageAndFormats = useCallback(
    async (data: LanguageAndFormatsFormValues, onDiscard: () => void) => {
      let success: boolean = true
      try {
        const thunkResult = await dispatch(updateSessionUserConfiguration(data))

        if (!updateSessionUserConfiguration.fulfilled.match(thunkResult)) {
          throw new Error(thunkResult.payload?.message ?? 'Internal error')
        }

        if (isUnMounted()) return

        onDiscard()
      } catch (e) {
        success = false
      }

      setTimeout(() => {
        showNotificationToast(success, toast)
      }, 1000)
    },
    [dispatch, toast, isUnMounted],
  )

  return [languageAndPreferences, updateLanguageAndFormats] as const
}

/**
 * this function has to access the intl object directly
 * otherwise using the standard useTexts hooks
 * will raise the issue of having the text translated into
 * the previous language
 */
function showNotificationToast(success: boolean, toast: ReturnType<typeof useNotification>) {
  const toastOptions = {
    autoClose: 5000,
  }

  if (success) {
    toast.success(
      intl.translate({
        id: 'settings.languageAndFormats.edit.save.success',
      }),
      toastOptions,
    )
    return
  }

  toast.error(
    intl.translate({
      id: 'settings.languageAndFormats.edit.save.error',
    }),
    toastOptions,
  )
}
